import { useUpdateSuperAdminShopPlan } from "@babackapp/private-api-client/client/use-super-admin-shop-configuration";
import type {
  SuperAdminPlanFieldDto,
  SuperAdminShopPlanDto,
  SuperAdminUpdateShopPlanBodyDto,
} from "@babackapp/private-api-client/types";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Switch } from "@babackapp/ui/components/switch";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@babackapp/ui/components/toggle-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@babackapp/ui/components/alert-dialog";
import { useState } from "react";
import { parseAmount, parseCount } from "./form-values";
import { ConfigurationSection } from "./shop-account-sections";

type PlanValue = string | number | boolean | null | undefined;

/** The fields come from the server (`PLAN_FIELDS`): no list of our own. */
function valueOf(plan: SuperAdminShopPlanDto, field: string): PlanValue {
  return (plan as unknown as Record<string, PlanValue>)[field];
}

const SWITCH_GROUPS: Array<{
  group: SuperAdminPlanFieldDto["group"];
  title: string;
}> = [
  { group: "returns", title: "Returns" },
  { group: "integrations", title: "Intégrations" },
  { group: "tracking", title: "Tracking" },
];

const DEFAULT = "default";
const ON = "on";
const OFF = "off";

function toChoice(value: PlanValue): string {
  if (value == null) return DEFAULT;
  return value ? ON : OFF;
}

function fromChoice(choice: string): boolean | null {
  if (choice === DEFAULT) return null;
  return choice === ON;
}

const SELECTED_ITEM =
  "flex-none px-2.5 text-xs whitespace-nowrap data-[state=on]:bg-violet-600 data-[state=on]:text-white";

function FeatureLine({
  descriptor,
  value,
  isPending,
  isIgnored = false,
  onChange,
}: {
  descriptor: SuperAdminPlanFieldDto;
  value: PlanValue;
  isPending: boolean;
  /** Retraction-only mode is on and the office locks this option. */
  isIgnored?: boolean;
  onChange: (value: boolean | null) => void;
}) {
  const isDefaultable = descriptor.kind === "default";

  return (
    <div
      className={`flex items-center justify-between gap-4 px-5 py-2.5 ${
        isIgnored ? "opacity-50" : ""
      }`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{descriptor.label}</span>
          {isDefaultable && (
            <span className="text-xs text-muted-foreground">
              défaut : {descriptor.isOnByDefault ? "oui" : "non"}
            </span>
          )}
          {isIgnored && (
            <span className="text-xs text-amber-700">
              sans effet : mode rétractation
            </span>
          )}
        </div>
        {descriptor.description && (
          <p className="text-xs text-muted-foreground">
            {descriptor.description}
          </p>
        )}
      </div>
      {isDefaultable ? (
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={toChoice(value)}
          disabled={isPending}
          // Radix sends "" when the selected item is clicked again: keep it.
          onValueChange={(choice) => choice && onChange(fromChoice(choice))}
          className="shrink-0"
        >
          <ToggleGroupItem value={DEFAULT} className={SELECTED_ITEM}>
            Défaut
          </ToggleGroupItem>
          <ToggleGroupItem value={ON} className={SELECTED_ITEM}>
            Oui
          </ToggleGroupItem>
          <ToggleGroupItem value={OFF} className={SELECTED_ITEM}>
            Non
          </ToggleGroupItem>
        </ToggleGroup>
      ) : (
        <Switch
          checked={value === true}
          disabled={isPending}
          onCheckedChange={(checked) => onChange(checked)}
        />
      )}
    </div>
  );
}

/** Parsed value of an offer input; `undefined` = invalid, saving refused. */
function parseOfferValue(
  descriptor: SuperAdminPlanFieldDto,
  text: string,
): string | number | null | undefined {
  const parsed =
    descriptor.kind === "integer"
      ? parseCount(text, descriptor.max ?? undefined)
      : descriptor.kind === "number"
        ? parseAmount(text, descriptor.max ?? undefined)
        : text.trim() || null;
  if (parsed === null && descriptor.isRequired) return undefined;
  return parsed;
}

/** Code, price, quotas: a form, saved together. */
function OfferForm({
  plan,
  descriptors,
  isPending,
  onSave,
}: {
  plan: SuperAdminShopPlanDto;
  descriptors: SuperAdminPlanFieldDto[];
  isPending: boolean;
  onSave: (body: SuperAdminUpdateShopPlanBodyDto) => void;
}) {
  const initial = Object.fromEntries(
    descriptors.map(({ field }) => [
      field,
      valueOf(plan, field)?.toString() ?? "",
    ]),
  );
  const [form, setForm] = useState(initial);
  const parsed = Object.fromEntries(
    descriptors.map((descriptor) => [
      descriptor.field,
      parseOfferValue(descriptor, form[descriptor.field] ?? ""),
    ]),
  );
  const invalidFields = descriptors.filter(
    ({ field }) => parsed[field] === undefined,
  );
  const isDirty = JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <div className="grid grid-cols-4 items-start gap-4 px-5 py-4">
      {descriptors.map(({ field, label, isRequired }) => (
        <label key={field} className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">
            {label}
            {isRequired && " *"}
          </span>
          <Input
            value={form[field] ?? ""}
            aria-invalid={parsed[field] === undefined}
            className="aria-[invalid=true]:border-destructive"
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                [field]: event.target.value,
              }))
            }
          />
        </label>
      ))}
      <div className="col-span-4 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Transporteurs fournis par Baback :{" "}
          {plan.transportCarrierCodes.length
            ? plan.transportCarrierCodes.join(", ")
            : "aucun"}
          {invalidFields.length > 0 && (
            <span className="ml-3 text-destructive">
              Valeur invalide :{" "}
              {invalidFields.map(({ label }) => label).join(", ")}
            </span>
          )}
        </p>
        <Button
          size="sm"
          disabled={!isDirty || invalidFields.length > 0 || isPending}
          onClick={() =>
            onSave(
              Object.fromEntries(
                descriptors
                  .filter(({ field }) => form[field] !== initial[field])
                  .map(({ field }) => [field, parsed[field]]),
              ) as SuperAdminUpdateShopPlanBodyDto,
            )
          }
        >
          {isPending ? "Enregistrement…" : "Enregistrer l'offre"}
        </Button>
      </div>
    </div>
  );
}

type DeactivatedFeature = { field: string; label: string };

/** The features a 409 `plan-removal-deactivates-features` asks to confirm. */
function featuresToConfirm(error: unknown): DeactivatedFeature[] | null {
  if (typeof error !== "object" || error === null) return null;
  const { code, features } = error as { code?: unknown; features?: unknown };
  return code === "plan-removal-deactivates-features" && Array.isArray(features)
    ? (features as DeactivatedFeature[])
    : null;
}

/** The shop's plan, edited by Care (BBS-406): each switch saves at once. */
export function ShopPlanSection({
  shopId,
  plan,
  planFields,
}: {
  shopId: string;
  plan: SuperAdminShopPlanDto;
  planFields: SuperAdminPlanFieldDto[];
}) {
  const updateMutation = useUpdateSuperAdminShopPlan(shopId);
  const offerFields = planFields.filter(({ group }) => group === "offer");
  const modeFields = planFields.filter(({ group }) => group === "mode");
  const isRetractionOnly = modeFields.some(
    ({ field }) => valueOf(plan, field) === true,
  );
  // A change that would switch features off in the shop, waiting for the
  // super admin's confirmation (409 from the backend, which lists them).
  const [pending, setPending] = useState<{
    body: SuperAdminUpdateShopPlanBodyDto;
    features: DeactivatedFeature[];
  } | null>(null);

  function save(body: SuperAdminUpdateShopPlanBodyDto) {
    updateMutation.mutate(
      { params: { path: { shopId } }, body },
      {
        onError: (error) => {
          const features = featuresToConfirm(error);
          if (features) setPending({ body, features });
        },
      },
    );
  }

  function confirm() {
    if (!pending) return;
    const { body } = pending;
    setPending(null);
    save({ ...body, confirmDeactivation: true });
  }

  function cancel() {
    setPending(null);
    // The 409 is no failure to report: nothing was asked to be saved yet.
    updateMutation.reset();
  }

  return (
    <ConfigurationSection
      title="Plan"
      description="Ce que la boutique a souscrit. Chaque interrupteur s'enregistre aussitôt ; « Défaut » laisse la plateforme décider."
    >
      <OfferForm
        key={JSON.stringify(
          offerFields.map(({ field }) => valueOf(plan, field)),
        )}
        plan={plan}
        descriptors={offerFields}
        isPending={updateMutation.isPending}
        onSave={save}
      />
      {modeFields.length > 0 && (
        <div className={`border-t ${isRetractionOnly ? "bg-amber-50" : ""}`}>
          {modeFields.map((descriptor) => (
            <FeatureLine
              key={descriptor.field}
              descriptor={descriptor}
              value={valueOf(plan, descriptor.field)}
              isPending={updateMutation.isPending}
              onChange={(value) => save({ [descriptor.field]: value })}
            />
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 border-t lg:grid-cols-3 lg:divide-x">
        {SWITCH_GROUPS.map(({ group, title }) => (
          <div key={group} className="py-2">
            <h3 className="px-5 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {title}
            </h3>
            {planFields
              .filter((descriptor) => descriptor.group === group)
              .map((descriptor) => (
                <FeatureLine
                  key={descriptor.field}
                  descriptor={descriptor}
                  value={valueOf(plan, descriptor.field)}
                  isPending={updateMutation.isPending}
                  isIgnored={
                    isRetractionOnly && descriptor.isIgnoredInRetractionMode
                  }
                  onChange={(value) => save({ [descriptor.field]: value })}
                />
              ))}
          </div>
        ))}
      </div>
      {updateMutation.isError && !pending && (
        <p className="border-t px-5 py-3 text-sm text-destructive">
          L'enregistrement a échoué, rien n'a été modifié.
        </p>
      )}
      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => !open && cancel()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Désactiver aussi dans la boutique ?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Ces options sont actives dans la boutique. Les retirer du plan
                  les désactive aussi pour le marchand :
                </p>
                <ul className="list-disc pl-5 font-medium text-foreground">
                  {pending?.features.map(({ field, label }) => (
                    <li key={field}>{label}</li>
                  ))}
                </ul>
                <p>
                  Les réglages sont conservés : remettre l'option dans le plan
                  ne la réactive pas, le marchand le fait lui-même.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirm}>
              Retirer et désactiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfigurationSection>
  );
}
